import React, { useEffect } from 'react';
// Components for Landing Page Sections
import { motion, useScroll, useSpring } from 'framer-motion';
import { HeroSection } from './HeroSection';
import { FeaturesSection } from './FeaturesSection';
import { AISection } from './AISection';
import { HowItWorksSection } from './HowItWorksSection';
import { FAQSection } from './FAQSection';
import { CTASection } from './CTASection';
import { LandingNavbar } from './LandingNavbar';
import { LandingFooter } from './LandingFooter';
import { SEO } from '../SEO';
import './landing.css';

interface LandingPageProps {
    onGetStarted: () => void;
    onHelpCenter?: () => void;
    onPrivacyPolicy?: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted, onHelpCenter, onPrivacyPolicy }) => {
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });

    // Enable scroll on mount, disable on unmount (reverts to index.html CSS)
    useEffect(() => {
        document.body.style.overflow = 'auto';
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    const faqs = [
        {
            question: "Is NoteSpace really free?",
            answer: "Yes, our core plan is free forever for individuals. This includes unlimited boards, basic AI usage, and all core note-taking features."
        },
        {
            question: "How does the Smart View AI work?",
            answer: "Smart View uses Google's Gemini models to analyze the text, images, and documents on your current board. It can answer questions, summarize content, and generate study materials based ONLY on your provided context."
        },
        {
            question: "Can I collaborate with others?",
            answer: "Absolutely. You can share boards via a simple link or invite members to your workspace. Changes sync in real-time, and you can see collaborators' cursors."
        },
        {
            question: "Is my data private?",
            answer: "We prioritize privacy. Your notes are stored securely, and AI processing is done ephemerally for Smart View requests. We do not train models on your private data."
        },
        {
            question: "Can I export my notes?",
            answer: "Yes, you can export your boards as PDF, PNG images, or Markdown files. You can also copy structured JSON data if needed."
        }
    ];

    const schema = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "SoftwareApplication",
                "name": "NoteSpace",
                "applicationCategory": "ProductivityApplication",
                "operatingSystem": "Web",
                "offers": {
                    "@type": "Offer",
                    "price": "0",
                    "priceCurrency": "USD"
                },
                "description": "Infinite canvas for visual thinkers. Organize ideas spatially, collaborate in real-time, and use AI to generate quizzes and summaries from your notes.",
                "screenshot": "https://www.notesscape.in/hero-preview.png"
            },
            {
                "@type": "Organization",
                "name": "NoteSpace",
                "url": "https://www.notesscape.in",
                "logo": "https://www.notesscape.in/notescape.png",
                "sameAs": [
                    "https://twitter.com/notescape",
                    "https://github.com/jainis5599/NotesScape2.0"
                ]
            },
            {
                "@type": "WebSite",
                "name": "NoteSpace",
                "url": "https://www.notesscape.in",
                "potentialAction": {
                    "@type": "SearchAction",
                    "target": "https://www.notesscape.in/search?q={search_term_string}",
                    "query-input": "required name=search_term_string"
                }
            },
            {
                "@type": "HowTo",
                "name": "How to Get Started with NoteSpace",
                "step": [
                    {
                        "@type": "HowToStep",
                        "text": "Sign up for a free account to access your personal workspace."
                    },
                    {
                        "@type": "HowToStep",
                        "text": "Create your first infinite canvas board by clicking the 'New Board' button."
                    },
                    {
                        "@type": "HowToStep",
                        "text": "Add notes, images, and documents using the toolbar or drag-and-drop."
                    },
                    {
                        "@type": "HowToStep",
                        "text": "Use Smart View AI to generate summaries and study guides from your content."
                    }
                ]
            },
            {
                "@type": "FAQPage",
                "mainEntity": faqs.map(faq => ({
                    "@type": "Question",
                    "name": faq.question,
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": faq.answer
                    }
                }))
            }
        ]
    };

    return (
        <div className="landing-page">
            <SEO
                title="NoteSpace | Infinite Canvas for Visual Thinkers & AI Research"
                description="Unlock your visual thinking with NoteSpace. The infinite whiteboard for spatial organization, real-time collaboration, and AI-powered study tools."
                keywords={["infinite canvas", "ai note taking", "visual collaboration", "online whiteboard", "freeform notes", "spatial organization", "knowledge graph", "study tools"]}
                url="https://www.notesscape.in/"
            />

            {/* Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify(schema)}
            </script>

            <motion.div
                className="fixed top-0 left-0 right-0 h-1 bg-blue-500 origin-left z-50"
                style={{ scaleX }}
            />

            <LandingNavbar onGetStarted={onGetStarted} />

            <main>
                <HeroSection onGetStarted={onGetStarted} />
                <FeaturesSection />
                <AISection />
                <HowItWorksSection />
                <FAQSection />
                <CTASection onGetStarted={onGetStarted} />
            </main>

            <LandingFooter onHelpCenter={onHelpCenter} onPrivacyPolicy={onPrivacyPolicy} />
        </div>
    );
};
